// Seasonings manager — the household's spice rack. Tick the seasonings you
// keep on hand; that stocked set drives which seasonings come pre-checked
// when a recipe calls for one (see the add-to-list seasonings picker).
//
// Opened from the Pantry toolbar. Saves the whole picker state in one
// seasonings.setAll write so the household lands in the "configured" state.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type SeasoningCatalogItem } from "../../lib/rpc";

interface SeasoningsManagerProps {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
}

export function SeasoningsManager({ open, onClose, notify }: SeasoningsManagerProps) {
  const [catalog, setCatalog] = useState<SeasoningCatalogItem[] | null>(null);
  const [stocked, setStocked] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [newCustom, setNewCustom] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCatalog(null);
    api.seasoningsCatalog().then((items) => {
      if (cancelled) return;
      setCatalog(items);
      setStocked(new Set(items.filter((s) => s.stocked).map((s) => s.name)));
    }).catch((e: any) => {
      notify(e?.message || "couldn't load seasonings");
      setCatalog([]);
    });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Group the flat catalog into its cosmetic sections, preserving the
  // catalog's order within each group.
  const groups = useMemo(() => {
    const m = new Map<string, SeasoningCatalogItem[]>();
    for (const s of catalog || []) {
      if (!m.has(s.group)) m.set(s.group, []);
      m.get(s.group)!.push(s);
    }
    return [...m.entries()];
  }, [catalog]);

  if (!open) return null;

  const toggle = (name: string) =>
    setStocked((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.seasoningsSetAll([...stocked]);
      notify(`Saved — ${stocked.size} on hand`);
      onClose();
    } catch (e: any) {
      notify(e?.message || "couldn't save seasonings");
    } finally {
      setSaving(false);
    }
  };

  // Add a custom seasoning. Persists immediately (its own row), then merges
  // optimistically into the open editor so the user's other unsaved toggles
  // survive — Save still writes the full stocked set including this one.
  const addCustom = async () => {
    const name = newCustom.trim();
    if (!name || addingCustom) return;
    setAddingCustom(true);
    try {
      const saved = await api.seasoningsAddCustom(name);
      setCatalog((prev) => prev
        ? (prev.some((s) => s.name.toLowerCase() === saved.toLowerCase())
            ? prev
            : [...prev, { name: saved, group: "Your own", stocked: true, configured: true, custom: true }])
        : prev);
      setStocked((prev) => new Set(prev).add(saved));
      setNewCustom("");
    } catch (e: any) {
      notify(e?.message || "couldn't add seasoning");
    } finally {
      setAddingCustom(false);
    }
  };

  const removeCustom = async (name: string) => {
    try {
      await api.seasoningsRemoveCustom(name);
      setCatalog((prev) => prev ? prev.filter((s) => s.name !== name) : prev);
      setStocked((prev) => { const n = new Set(prev); n.delete(name); return n; });
    } catch (e: any) {
      notify(e?.message || "couldn't remove");
    }
  };

  return (
    <div className="list-picker-bg" onClick={onClose}>
      <div className="seasonings-mgr" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Pantry staples you keep on hand">
        <header className="seasonings-mgr-head">
          <div>
            <span className="seasonings-mgr-title">Pantry staples</span>
            <p className="seasonings-mgr-sub">
              Tick what you keep on hand — seasonings, oils, baking staples like flour
              and sugar. When a recipe needs something you're out of, we'll offer to add
              it to your list; the rest stay off the cart.
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        {catalog === null && <p className="list-picker-loading">Loading…</p>}

        {catalog !== null && (
          <div className="seasonings-mgr-body">
            {groups.map(([group, items]) => (
              <section key={group} className="seasonings-mgr-group">
                <div className="seasonings-mgr-group-label">{group}</div>
                <div className="seasonings-mgr-grid">
                  {items.map((s) => {
                    const on = stocked.has(s.name);
                    const chip = (
                      <button
                        type="button"
                        className="seasonings-chip"
                        data-on={on}
                        onClick={() => toggle(s.name)}
                        aria-pressed={on}
                      >
                        <span className="seasonings-chip-check" aria-hidden>
                          {on && <Icon name="check" size={12} />}
                        </span>
                        <span>{s.name}</span>
                      </button>
                    );
                    // Custom entries get a remove affordance (kept a sibling,
                    // not nested, so we don't put a button inside a button).
                    return s.custom ? (
                      <span key={s.name} className="seasonings-chip-wrap">
                        {chip}
                        <button
                          type="button"
                          className="seasonings-chip-remove"
                          title={`Remove ${s.name}`}
                          aria-label={`Remove ${s.name}`}
                          onClick={() => removeCustom(s.name)}
                        >
                          <Icon name="x" size={11} />
                        </button>
                      </span>
                    ) : (
                      <span key={s.name}>{chip}</span>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Add your own — persists immediately; toggles below it survive. */}
            <section className="seasonings-mgr-group">
              <div className="seasonings-mgr-group-label">Add your own</div>
              <div className="seasonings-mgr-add">
                <input
                  placeholder="e.g. MSG, za'atar, everything bagel…"
                  value={newCustom}
                  maxLength={40}
                  onInput={(e) => setNewCustom((e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addCustom(); } }}
                  disabled={addingCustom}
                />
                <button
                  className="manage-tags-add-btn"
                  type="button"
                  onClick={addCustom}
                  disabled={!newCustom.trim() || addingCustom}
                >
                  Add
                </button>
              </div>
            </section>
          </div>
        )}

        <div className="seasonings-mgr-actions">
          <span className="seasonings-mgr-count">{stocked.size} on hand</span>
          <div className="seasonings-mgr-actions-btns">
            <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || catalog === null} type="button">
              <Icon name="check" size={13} />
              <span>{saving ? "Saving…" : "Save"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
